/* Copper Studio — behaviour for cs-* sections. Loaded from layout/theme.liquid. */

/**
 * Daily countdown: always counts down to the next midnight in UK time (Europe/London),
 * including the summer/winter clock change, then restarts automatically.
 */
class CsCountdown extends HTMLElement {
  connectedCallback() {
    this.parts = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]'),
    };
    const daysUnit = this.parts.days?.parentElement;
    if (daysUnit) daysUnit.hidden = true;
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  secondsToUkMidnight() {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date())
      .forEach((p) => (parts[p.type] = Number(p.value)));
    const elapsed = parts.hour * 3600 + parts.minute * 60 + parts.second;
    const left = 86400 - elapsed;
    return left >= 86400 ? 0 : left;
  }

  tick() {
    const left = this.secondsToUkMidnight();
    const pad = (n) => String(n).padStart(2, '0');
    const values = {
      hours: Math.floor(left / 3600),
      minutes: Math.floor((left % 3600) / 60),
      seconds: left % 60,
    };
    for (const [key, value] of Object.entries(values)) {
      if (this.parts[key]) this.parts[key].textContent = pad(value);
    }
  }
}

/**
 * Horizontal scroller with prev/next buttons and tap-to-play videos.
 */
class CsScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.controller = new AbortController();
    const { signal } = this.controller;

    for (const btn of this.querySelectorAll('[data-dir]')) {
      btn.addEventListener('click', () => this.scrollByCard(Number(btn.dataset.dir)), { signal });
    }
    this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true, signal });
    this.track.addEventListener('click', (event) => this.handlePlay(event), { signal });
    this.buildDots();
    this.updateButtons();
  }

  buildDots() {
    this.dots = this.querySelector('[data-dots]');
    if (!this.dots) return;
    this.dots.replaceChildren(
      ...[...this.track.children].map(() => {
        const dot = document.createElement('span');
        dot.className = 'cs-scroller__dot';
        return dot;
      })
    );
  }

  updateDots() {
    if (!this.dots?.children.length) return;
    const pos = Math.abs(this.track.scrollLeft);
    const max = this.track.scrollWidth - this.track.clientWidth;
    const items = [...this.track.children];
    let active = 0;
    if (max > 0 && pos >= max - 2) {
      active = items.length - 1;
    } else {
      const start = this.track.getBoundingClientRect().left + parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart || 0);
      let best = Infinity;
      items.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - start);
        if (distance < best) {
          best = distance;
          active = index;
        }
      });
    }
    [...this.dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === active));
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  scrollByCard(direction) {
    const card = this.track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : 240;
    this.track.scrollBy({ left: step * direction * (document.dir === 'rtl' ? -1 : 1), behavior: 'smooth' });
  }

  updateButtons() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const pos = Math.abs(this.track.scrollLeft);
    const prev = this.querySelector('[data-dir="-1"]');
    const next = this.querySelector('[data-dir="1"]');
    if (prev) prev.disabled = pos <= 2;
    if (next) next.disabled = pos >= max;
    this.updateDots();
  }

  handlePlay(event) {
    const target = event.target instanceof Element ? event.target : null;
    const card = target?.closest('.cs-video-card');
    const video = card?.querySelector('video');
    if (!card || !video) return;
    if (video.paused) {
      for (const other of this.track.querySelectorAll('video')) {
        if (other !== video) {
          other.pause();
          other.muted = true;
          other.closest('.cs-video-card')?.classList.remove('is-playing');
        }
      }
      // Started by a tap, so the browser allows sound.
      video.muted = false;
      video.volume = 1;
      video.play().catch((error) => {
        // Only fall back to silent playback if the browser blocks sound.
        if (error?.name !== 'NotAllowedError') return;
        video.muted = true;
        video.play().catch(() => {});
      });
      card.classList.add('is-playing');
    } else {
      video.pause();
      card.classList.remove('is-playing');
    }
  }
}

if (!customElements.get('cs-countdown')) customElements.define('cs-countdown', CsCountdown);
if (!customElements.get('cs-scroller')) customElements.define('cs-scroller', CsScroller);

/**
 * Add-on card ("Make it a gift"): when ticked, adds the add-on variant to the cart right before the
 * main product's Add to cart runs, so both appear together in the cart drawer.
 */
class CsAddon extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.controller = new AbortController();
    this.section.addEventListener('click', (event) => this.handleClick(event), { capture: true, signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button[name="add"], [type="submit"][name="add"]');
    const check = this.querySelector('[data-addon-check]');
    if (!button || !check?.checked || button.dataset.csAddonDone === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.dataset.variantId), quantity: 1 }] }),
      });
    } catch (error) {
      console.warn('Add-on could not be added', error);
    }
    check.checked = false;
    button.dataset.csAddonDone = 'true';
    button.click();
    window.setTimeout(() => delete button.dataset.csAddonDone, 1500);
  }
}

if (!customElements.get('cs-addon')) customElements.define('cs-addon', CsAddon);
